import { useCallback, useEffect, useState } from 'react';

import { darkModeSupported } from 'virtual:pwa-theme-meta';

import { COLOR_SCHEME_PREFERENCE_KEY } from '@/constants';
import { useMediaQuery } from './use-media-query';

/** What the reader chose, persisted in localStorage. `system` follows the OS. */
export type ColorSchemePreference = 'system' | 'light' | 'dark';

/** What actually gets applied to `<html data-theme>` -- `system` is resolved away before this point. */
export type ColorScheme = 'light' | 'dark';

function readStoredPreference(): ColorSchemePreference {
	const stored = localStorage.getItem(COLOR_SCHEME_PREFERENCE_KEY);
	if (stored === 'system' || stored === 'light' || stored === 'dark') {
		return stored;
	}
	// No preference saved yet (or a corrupted value) follows the OS. Safe for
	// every tenant since the contrast audit: one whose accent fails on the
	// dark background has `darkModeSupported: false` and resolves to light
	// below whatever this returns. index-template.ejs's pre-paint script
	// applies the same default.
	return 'system';
}

export interface UseColorSchemeResult {
	/** The resolved value -- what the compiled theme CSS actually keys off. */
	colorScheme: ColorScheme;
	/** The reader's raw choice, e.g. for a control to show which option is selected. */
	preference: ColorSchemePreference;
	/** Persists the choice and applies it immediately. Called by the Settings dialog's Theme control. */
	setColorSchemePreference: (preference: ColorSchemePreference) => void;
}

/**
 * The app's current color scheme, kept in sync with `<html data-theme>` --
 * the attribute the compiled theme CSS keys its dark values off
 * (`:root[data-theme='dark']`, see scripts/lib/theme/compile.ts and
 * docs/adr/0007-dark-mode-selection.md).
 *
 * The *initial* value is resolved before first paint by the inline script in
 * index-template.ejs (same localStorage key, same resolution rule), so
 * there is no flash-of-wrong-theme -- this hook does not own that moment.
 * What it does own is keeping the attribute (and the React value read off
 * AppContext) live for the rest of the session: a `system` preference
 * reacting to an OS change while the app stays open, and any change made
 * through `setColorSchemePreference` (the Settings dialog's Theme control).
 */
export function useColorScheme(): UseColorSchemeResult {
	const [preference, setPreference] = useState<ColorSchemePreference>(
		readStoredPreference,
	);
	const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

	// A tenant whose accent vanishes on the dark background (see
	// scripts/lib/theme/contrast.ts) stays light whatever was saved -- the
	// reader menu doesn't offer dark there either.
	const colorScheme: ColorScheme = !darkModeSupported
		? 'light'
		: preference === 'system'
			? prefersDark
				? 'dark'
				: 'light'
			: preference;

	useEffect(() => {
		document.documentElement.setAttribute('data-theme', colorScheme);
	}, [colorScheme]);

	const setColorSchemePreference = useCallback(
		(next: ColorSchemePreference) => {
			localStorage.setItem(COLOR_SCHEME_PREFERENCE_KEY, next);
			setPreference(next);
		},
		[],
	);

	return { colorScheme, preference, setColorSchemePreference };
}
